import { useQuery } from '@tanstack/react-query';
import Input from '../../components/common/Input';
import Select from '../../components/common/Select';
import { ClientOnboardingFormData, ClientType } from '../../types';
import api from '../../services/api';

interface ConfigurationTabProps {
  formData: ClientOnboardingFormData;
  errors: Record<string, string>;
  onInputChange: (field: keyof ClientOnboardingFormData, value: any) => void;
  /** Present = edit mode; used to load the client's current space usage. */
  clientId?: number;
}

export default function ConfigurationTab({
  formData,
  errors,
  onInputChange,
  clientId,
}: ConfigurationTabProps) {
  const isEdit = clientId != null;

  // Fetch space utilization info when editing
  const { data: spaceInfo, isLoading: spaceLoading } = useQuery({
    queryKey: ['client-space-info', clientId],
    queryFn: async () => {
      if (clientId == null) return null;
      try {
        const response = await api.get(`/clients/${clientId}/space-info`);
        return response?.data || null;
      } catch (error) {
        console.error('Error fetching space info:', error);
        return null;
      }
    },
    enabled: isEdit,
  });

  const clientTypeOptions = [
    { value: 'per_pallet', label: 'Per Pallet' },
    { value: 'per_sqft', label: 'Per Square Feet' },
    { value: 'both', label: 'Both (Rack + Zone)' }
  ];

  return (
    <div className="space-y-6">
      {/* Space Utilization Info - Only show when editing */}
      {isEdit && (
        <div className="border-t pt-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <svg className="w-5 h-5 text-brand-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            Current Space Utilization
          </h3>
          
          {spaceLoading ? (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600 mr-3"></div>
                <span className="text-gray-600">Loading space utilization data...</span>
              </div>
            </div>
          ) : spaceInfo && spaceInfo.clientType === 'both' ? (
            <div className="bg-gradient-to-br from-brand-50 to-brand-50 border-2 border-brand-200 rounded-xl p-6 shadow-sm">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Zone Storage (Sq Ft) */}
                <div className="bg-white rounded-lg p-5 shadow-sm border border-green-100">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-semibold text-gray-700">Zone Storage (Sq Ft)</h4>
                    <span className="text-lg font-bold text-gray-900">{spaceInfo.zoneUtilizationPercentage || 0}%</span>
                  </div>
                  <p className="text-2xl font-bold text-green-700">
                    {(spaceInfo.zoneSqftUsed ?? 0).toLocaleString()}
                    <span className="text-sm font-medium text-gray-500"> / {(spaceInfo.zoneSqftLimit ?? 0).toLocaleString()} sqft</span>
                  </p>
                  <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden mt-3">
                    <div
                      className={`h-3 rounded-full transition-all duration-500 ${
                        (spaceInfo.zoneUtilizationPercentage || 0) >= 90
                          ? 'bg-gradient-to-r from-red-500 to-red-600'
                          : (spaceInfo.zoneUtilizationPercentage || 0) >= 70
                          ? 'bg-gradient-to-r from-yellow-500 to-yellow-600'
                          : 'bg-gradient-to-r from-green-500 to-green-600'
                      }`}
                      style={{ width: `${Math.min(spaceInfo.zoneUtilizationPercentage || 0, 100)}%` }}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-2">{Math.max(0, spaceInfo.zoneSqftAvailable ?? 0).toLocaleString()} sqft remaining</p>
                </div>

                {/* Rack Storage (Pallets) */}
                <div className="bg-white rounded-lg p-5 shadow-sm border border-brand-100">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-semibold text-gray-700">Rack Storage (Pallets)</h4>
                    <span className="text-lg font-bold text-gray-900">{spaceInfo.rackUtilizationPercentage || 0}%</span>
                  </div>
                  <p className="text-2xl font-bold text-brand-700">
                    {(spaceInfo.rackPalletsUsed ?? 0).toLocaleString()}
                    <span className="text-sm font-medium text-gray-500"> / {(spaceInfo.rackPalletLimit ?? 0).toLocaleString()} pallets</span>
                  </p>
                  <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden mt-3">
                    <div
                      className={`h-3 rounded-full transition-all duration-500 ${
                        (spaceInfo.rackUtilizationPercentage || 0) >= 90
                          ? 'bg-gradient-to-r from-red-500 to-red-600'
                          : (spaceInfo.rackUtilizationPercentage || 0) >= 70
                          ? 'bg-gradient-to-r from-yellow-500 to-yellow-600'
                          : 'bg-gradient-to-r from-green-500 to-green-600'
                      }`}
                      style={{ width: `${Math.min(spaceInfo.rackUtilizationPercentage || 0, 100)}%` }}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-2">{Math.max(0, spaceInfo.rackPalletsAvailable ?? 0).toLocaleString()} pallets remaining</p>
                </div>
              </div>

              {spaceInfo.additionalSpaceAllocated > 0 && (
                <p className="mt-4 text-xs text-orange-700 bg-orange-50 border border-orange-200 rounded-lg px-3 py-2">
                  This client is using {spaceInfo.additionalSpaceAllocated.toLocaleString()} sqft of additional space beyond their allocation. Additional charges may apply.
                </p>
              )}
            </div>
          ) : spaceInfo ? (
            <div className="bg-gradient-to-br from-brand-50 to-brand-50 border-2 border-brand-200 rounded-xl p-6 shadow-sm">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                {/* Base Allocation */}
                <div className="bg-white rounded-lg p-4 shadow-sm border border-brand-100">
                  <div className="flex items-center mb-2">
                    <svg className="w-5 h-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <h4 className="text-sm font-semibold text-gray-700">Base Allocation</h4>
                  </div>
                  <p className="text-2xl font-bold text-gray-900">
                    {spaceInfo.clientType === 'per_pallet' || spaceInfo.clientType === 'pallet'
                      ? `${spaceInfo.baseAllocationPallets?.toLocaleString() || 0} pallets`
                      : `${spaceInfo.baseAllocation?.toLocaleString() || 0} sqft`
                    }
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {spaceInfo.clientType === 'per_pallet' || spaceInfo.clientType === 'pallet'
                      ? `≈ ${((spaceInfo.baseAllocationPallets ?? 0) * 25).toLocaleString()} sqft`
                      : `≈ ${Math.floor((spaceInfo.baseAllocation ?? 0) / 25).toLocaleString()} pallets`
                    }
                  </p>
                </div>

                {/* Space Used */}
                <div className="bg-white rounded-lg p-4 shadow-sm border border-brand-100">
                  <div className="flex items-center mb-2">
                    <svg className="w-5 h-5 text-brand-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                    </svg>
                    <h4 className="text-sm font-semibold text-gray-700">Currently Used</h4>
                  </div>
                  <p className="text-2xl font-bold text-brand-900">
                    {spaceInfo.palletsUsed?.toLocaleString() || 0} pallets
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    ≈ {((spaceInfo.palletsUsed ?? 0) * 25).toLocaleString()} sqft
                  </p>
                </div>

                {/* Space Available */}
                <div className="bg-white rounded-lg p-4 shadow-sm border border-brand-100">
                  <div className="flex items-center mb-2">
                    <svg className="w-5 h-5 text-purple-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
                    </svg>
                    <h4 className="text-sm font-semibold text-gray-700">Base Remaining</h4>
                  </div>
                  <p className="text-2xl font-bold text-purple-900">
                    {spaceInfo.clientType === 'per_pallet' || spaceInfo.clientType === 'pallet'
                      ? `${Math.max(0, (spaceInfo.baseAllocationPallets || 0) - (spaceInfo.palletsUsed || 0)).toLocaleString()} pallets`
                      : `${Math.max(0, (spaceInfo.baseAllocation || 0) - (spaceInfo.spaceUsed || 0)).toLocaleString()} sqft`
                    }
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {spaceInfo.clientType === 'per_pallet' || spaceInfo.clientType === 'pallet'
                      ? `≈ ${(Math.max(0, (spaceInfo.baseAllocationPallets || 0) - (spaceInfo.palletsUsed || 0)) * 25).toLocaleString()} sqft`
                      : `≈ ${Math.floor(Math.max(0, (spaceInfo.baseAllocation || 0) - (spaceInfo.spaceUsed || 0)) / 25).toLocaleString()} pallets`
                    }
                  </p>
                </div>
              </div>

              {/* Additional Space Warning */}
              {spaceInfo.additionalSpaceAllocated > 0 && (
                <div className="bg-orange-50 border-2 border-orange-300 rounded-lg p-4 mb-4">
                  <div className="flex items-start">
                    <svg className="w-6 h-6 text-orange-600 mr-3 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <div className="flex-1">
                      <h4 className="text-sm font-bold text-orange-900 mb-2">Additional Space in Use</h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-xs text-orange-700 mb-1">Additional Space</p>
                          <p className="text-lg font-bold text-orange-900">
                            {spaceInfo.additionalSpaceAllocated?.toLocaleString() || 0} sqft
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-orange-700 mb-1">Additional Pallets</p>
                          <p className="text-lg font-bold text-orange-900">
                            {spaceInfo.additionalSpaceAllocatedPallets?.toLocaleString() || 0} pallets
                          </p>
                        </div>
                      </div>
                      <p className="text-xs text-orange-700 mt-2">
                        This client is using space beyond their base allocation. Additional space charges may apply.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Utilization Bar */}
              <div className="bg-white rounded-lg p-4 shadow-sm border border-brand-100">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-semibold text-gray-700">Utilization</h4>
                  <span className="text-lg font-bold text-gray-900">
                    {spaceInfo.utilizationPercentage || 0}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                  <div
                    className={`h-3 rounded-full transition-all duration-500 ${
                      (spaceInfo.utilizationPercentage || 0) >= 90
                        ? 'bg-gradient-to-r from-red-500 to-red-600'
                        : (spaceInfo.utilizationPercentage || 0) >= 70
                        ? 'bg-gradient-to-r from-yellow-500 to-yellow-600'
                        : 'bg-gradient-to-r from-green-500 to-green-600'
                    }`}
                    style={{ width: `${Math.min(spaceInfo.utilizationPercentage || 0, 100)}%` }}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  {(spaceInfo.utilizationPercentage || 0) >= 90
                    ? 'Critical: Space is nearly full'
                    : (spaceInfo.utilizationPercentage || 0) >= 70
                    ? 'Warning: Space is filling up'
                    : 'Good: Adequate space available'
                  }
                </p>
              </div>
            </div>
          ) : (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-yellow-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-sm text-yellow-800">
                  Space utilization data is not available for this client.
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="border-t pt-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Client Contract</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Select
            label="Client Type"
            options={clientTypeOptions}
            value={formData.clientType}
            onChange={(e) => onInputChange('clientType', e.target.value as ClientType)}
            required
            error={errors.clientType}
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Status <span className="text-red-500">*</span>
            </label>
            <div className="flex items-center space-x-3">
              <button
                type="button"
                onClick={() => onInputChange('status', formData.status === 'active' ? 'inactive' : 'active')}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 ${
                  formData.status === 'active' ? 'bg-green-600' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    formData.status === 'active' ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
              <span className={`text-sm font-medium ${formData.status === 'active' ? 'text-green-600' : 'text-gray-500'}`}>
                {formData.status === 'active' ? 'Active' : 'Inactive'}
              </span>
            </div>
            {errors.status && (
              <p className="mt-1 text-sm text-red-600">{errors.status}</p>
            )}
          </div>
        </div>

        {formData.clientType === 'both' && (
          <p className="mt-4 -mb-2 text-sm text-gray-600 bg-brand-50 border border-brand-200 rounded-lg px-3 py-2">
            This client stores in both racks and zones. Set a <span className="font-semibold">pallet capacity</span> for rack storage and a <span className="font-semibold">square-feet allocation</span> for zone (floor) storage.
          </p>
        )}

        {/* Conditional fields based on client type */}
        {(formData.clientType === 'per_pallet' || formData.clientType === 'both') && (
          <div className="mt-4">
            <Input
              label={formData.clientType === 'both' ? 'Rack Capacity (Pallets)' : 'Pallet Capacity'}
              type="number"
              value={formData.palletCapacity ?? ""}
              onChange={(e) => onInputChange('palletCapacity', e.target.value === "" ? "" : parseInt(e.target.value))}
              required
              error={errors.palletCapacity}
              placeholder="Enter number of pallets"
            />
            {formData.palletCapacity && Number(formData.palletCapacity) > 0 && (
              <div className="mt-3 bg-gradient-to-br from-brand-50 to-brand-50 border-l-4 border-brand-500 rounded-lg shadow-sm overflow-hidden">
                <div className="p-4">
                  <div className="flex items-center mb-3">
                    <div className="w-10 h-10 bg-brand-500 rounded-lg flex items-center justify-center mr-3">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Space Allocation Summary</h4>
                      <p className="text-xs text-gray-500">Pallet-based storage</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 bg-white rounded-lg p-3 sm:p-4 shadow-sm">
                    <div className="sm:border-r border-gray-200 min-w-0">
                      <p className="text-xs font-medium text-gray-500 uppercase mb-1">Pallet Capacity</p>
                      <p className="text-xl sm:text-2xl font-bold text-brand-600 tabular-nums break-words">
                        {Number(formData.palletCapacity).toLocaleString()}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">Pallets</p>
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-gray-500 uppercase mb-1">Equivalent Space</p>
                      <p className="text-xl sm:text-2xl font-bold text-brand-600 tabular-nums break-words">
                        {(Number(formData.palletCapacity) * 25).toLocaleString()}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">Square Feet</p>
                    </div>
                  </div>
                  
                  <div className="mt-3 flex items-center text-xs text-brand-700 bg-brand-100 rounded px-3 py-2">
                    <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="font-medium">Conversion Rate: 1 Pallet = 25 Square Feet</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {(formData.clientType === 'per_sqft' || formData.clientType === 'both') && (
          <div className="mt-4">
            <Input
              label={formData.clientType === 'both' ? 'Zone Space (Square Feet)' : 'Square Feet Assigned'}
              type="number"
              value={formData.sqftCapacity ?? ""}
              onChange={(e) => onInputChange('sqftCapacity', e.target.value === "" ? "" : parseInt(e.target.value))}
              required
              error={errors.sqftCapacity}
              placeholder="Enter square feet"
            />
            {formData.sqftCapacity && Number(formData.sqftCapacity) > 0 && (
              <div className="mt-3 bg-gradient-to-br from-green-50 to-emerald-50 border-l-4 border-green-500 rounded-lg shadow-sm overflow-hidden">
                <div className="p-4">
                  <div className="flex items-center mb-3">
                    <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center mr-3">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Space Allocation Summary</h4>
                      <p className="text-xs text-gray-500">Area-based storage</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 bg-white rounded-lg p-3 sm:p-4 shadow-sm">
                    <div className="sm:border-r border-gray-200 min-w-0">
                      <p className="text-xs font-medium text-gray-500 uppercase mb-1">Floor Space</p>
                      <p className="text-xl sm:text-2xl font-bold text-green-600 tabular-nums break-words">
                        {Number(formData.sqftCapacity).toLocaleString()}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">Square Feet</p>
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-gray-500 uppercase mb-1">Equivalent Capacity</p>
                      <p className="text-xl sm:text-2xl font-bold text-emerald-600 tabular-nums break-words">
                        {Math.floor(Number(formData.sqftCapacity) / 25).toLocaleString()}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">Pallets</p>
                    </div>
                  </div>
                  
                  <div className="mt-3 flex items-center text-xs text-green-700 bg-green-100 rounded px-3 py-2">
                    <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="font-medium">Conversion Rate: 25 Square Feet = 1 Pallet</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}